import type {ContributorInfo, ItemPermissionLevel} from '@Panels/share-panel/share-panel.types';
import {FolderPermissionLevelValue} from '@Panels/share-panel/share-panel.types';
import {DesignPermissionLevel, FolderPermissionLevel} from '@Panels/share-panel/share-panel.types';
import type {FiltersHashmap} from '@Components/checkbox-filters/checkbox-filters.types';
import {closeMessageModal, openMessageModal} from '@Modals/message-modal';
import {FlatIconColor, FlatIconColorType, IconSize, IconType} from '@Components/icon-v2/icon.types';
import styles from './share-panel.module.scss';
import {isUserPAYG} from '@Libraries/user.library';
import {CHECKBOX_TYPE} from '@Components/checkbox';
import {ShareInTeamBarState} from '@/components/share-in-team-toggle-bar/share-in-team-toggle-bar.types';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';

export const getDesignPermissionLevelText = (permissionLevel: ItemPermissionLevel): string => {
  if (permissionLevel === DesignPermissionLevel.CAN_EDIT) {
    return window.i18next.t('pmwjs_can_edit');
  }

  if (permissionLevel === DesignPermissionLevel.CAN_VIEW) {
    return window.i18next.t('pmwjs_can_view');
  }

  return '';
};

export const getFolderPermissionLevelLabel = (filters: FiltersHashmap): string => {
  const checkedKeys = Object.keys(filters).filter((key) => filters[key]);

  if (checkedKeys.length === 0) return window.i18next.t('pmwjs_none');

  const firstKey = checkedKeys[0];
  const additionalCount = checkedKeys.length - 1;

  const baseText = getFolderPermissionLevelText(Number(firstKey) as FolderPermissionLevel);

  return additionalCount > 0 ? `${baseText} +${additionalCount}` : baseText;
};

export const getFolderPermissionLevelText = (permissionLevel: ItemPermissionLevel): string => {
  if (permissionLevel === FolderPermissionLevel.CAN_EDIT) {
    return window.i18next.t('pmwjs_can_edit');
  }

  if (permissionLevel === FolderPermissionLevel.CAN_ADD) {
    return window.i18next.t('pmwjs_can_add');
  }

  if (permissionLevel === FolderPermissionLevel.CAN_COPY) {
    return window.i18next.t('pmwjs_can_copy');
  }

  if (permissionLevel === FolderPermissionLevel.CAN_INVITE) {
    return window.i18next.t('pmwjs_can_invite');
  }

  return '';
};

export const openPermissionDeniedPanel = (message: string): void => {
  openMessageModal({
    title: window.i18next.t('pmwjs_permission_denied'),
    text: message,
    iconProps: {
      icon: 'icon-lock',
      type: IconType.NONE,
      className: 'content-body',
      size: IconSize.SIZE_ICON_32,
      flatIconType: {color: FlatIconColor.NEUTRAL, type: FlatIconColorType.DEFAULT},
    },
    iconContainerStyles: `${styles.warningIcon} ${styles.bgClass}`,
    ctaButton: {
      text: window.i18next.t('pmwjs_okay'),
      onClick: () => {
        closeMessageModal();
      },
      classes: styles.panelFooterButton,
    },
  });
};

export const getShareErrorMessage = (response: unknown, type: string): string => {
  let message = window.i18next.t('pmwjs_error_sharing_design');
  if (type === 'folder') {
    message = window.i18next.t('pmwjs_share_folder_error');
  }

  if (!(response && typeof response === 'object' && 'code' in response)) {
    return message;
  }

  switch (response.code) {
    case 'throttled':
      return window.i18next.t('pmwjs_error_sharing_design_throttled');

    case 'allowance_exhausted':
      return window.i18next.t('pmwjs_error_sharing_design_allowance_exhausted');

    case 'no_valid_emails':
      return window.i18next.t('pmwjs_error_sharing_design_no_valid_emails');

    case 'no_emails':
      return window.i18next.t('pmwjs_error_sharing_design_no_emails');

    default:
      return message;
  }
};

export const getProfilePictureUrl = (item: ContributorInfo): string => {
  return item.profilePictureUrl && item.profilePictureUrl !== '' ? item.profilePictureUrl : window.PMW.util.asset_url('images/default_profile_picture_small.png');
};

export const convertToFiltersHashmap = (permissions: FolderPermissionLevel[]): FiltersHashmap => {
  const map: FiltersHashmap = {};
  permissions.forEach((level) => {
    map[level] = true;
  });
  return map;
};

export const isFolderPermissionLevelChecked = (hashmap: FiltersHashmap, permissionLevel: FolderPermissionLevel): boolean => {
  return hashmap[permissionLevel.toString()];
};

export const getFolderCheckboxType = (hashmap: FiltersHashmap, permissionLevel: FolderPermissionLevel): CHECKBOX_TYPE => {
  return isFolderPermissionLevelChecked(hashmap, permissionLevel) ? CHECKBOX_TYPE.PRIMARY : CHECKBOX_TYPE.LIGHT;
};

export const getInitialSharedInTeamBarState = (isSharedInTeam: boolean): ShareInTeamBarState => {
  if (isSharedInTeam) {
    return ShareInTeamBarState.ACTIVE;
  }

  if (isUserPAYG()) {
    return ShareInTeamBarState.UPSELLING;
  }

  return ShareInTeamBarState.INACTIVE;
};

export const getLowercaseSelectedFolderPermissionsText = (filtersMap: FiltersHashmap): string => {
  return getSelectedFolderPermissionsText(filtersMap).toLowerCase();
};

export const getRemoveContributorErrorMessage = (response: unknown): string => {
  if (response && typeof response === 'object' && 'code' in response) {
    if (response.code === 'user_does_not_own_poster' || response.code === 'user_is_not_contributor' || response.code === 'user_does_not_own_folder') {
      return window.i18next.t('pmwjs_remove_permission_denied_message');
    }
  }

  return window.i18next.t('pmwjs_default_error_error_boundary');
};

const getSelectedFolderPermissionsText = (filtersMap: FiltersHashmap): string => {
  const isDesignPermission = (level: FolderPermissionLevel): boolean =>
    [FolderPermissionLevel.CAN_EDIT, FolderPermissionLevel.CAN_ADD, FolderPermissionLevel.CAN_COPY].includes(level);

  const designPermissions: string[] = [];
  let hasInvite = false;

  Object.values(FolderPermissionLevel)
    .filter((key) => typeof key === 'number')
    .forEach((key) => {
      if (!filtersMap[key as number]) return;

      if (key === FolderPermissionLevel.CAN_INVITE) {
        hasInvite = true;
      } else if (isDesignPermission(key as FolderPermissionLevel)) {
        const label = window.i18next.t(getFolderPermissionTranslationKey(key as FolderPermissionLevel));
        designPermissions.push(label);
      }
    });

  let result = '';

  if (designPermissions.length > 0) {
    const designPart = hasInvite || designPermissions.length === 1 ? designPermissions.join(', ') : formatFolderPermissionsTextWithAnd(designPermissions);

    result += window.i18next.t('pmwjs_can_x', {action: `${designPart} ${window.i18next.t('pmwjs_designs')}`});
  }

  if (hasInvite) {
    const inviteText = window.i18next.t('pmwjs_invite_others');
    result = result ? `${result} & ${inviteText}` : window.i18next.t('pmwjs_can_x', {action: inviteText});
  }

  return result;
};

const formatFolderPermissionsTextWithAnd = (items: string[]): string => {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} & ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
};

const getFolderPermissionTranslationKey = (level: FolderPermissionLevel): string => {
  switch (level) {
    case FolderPermissionLevel.CAN_EDIT:
      return 'pmwjs_edit';
    case FolderPermissionLevel.CAN_ADD:
      return 'pmwjs_add';
    case FolderPermissionLevel.CAN_COPY:
      return 'pmwjs_copy';
    case FolderPermissionLevel.CAN_INVITE:
      return 'pmwjs_invite_others';
    default:
      return '';
  }
};

export const trackGA4EventForLoginClick = (redirectUrl: string): void => {
  const FOLDER = 'folder',
    POSTER = 'poster';
  if (window.PMW.getUserId() === 'notloggedin') {
    if (typeof window.PMW.util.getMethodFromUrl === 'function' && window.PMW.util?.getMethodFromUrl(redirectUrl) === 'contribute') {
      const type = window.PMW.util?.getControllerFromUrl(redirectUrl);
      if (type === FOLDER) {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_FOLDER_LANDING_PAGE_NAV_LOGIN_CLICKED);
      } else if (type === POSTER) {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_DESIGN_LANDING_PAGE_NAV_LOGIN_CLICKED);
      }
    }
  }
};

const isFolderPermissionLevel = (value: number): value is FolderPermissionLevel => {
  return Object.values(FolderPermissionLevel).includes(value);
};

/**
 * a permissions update only ever toggles one checkbox, so the single differing key identifies which one.
 * returns null when the two maps agree.
 */
export const getChangedPermissionLevel = (updatedPermissionsHashmap: FiltersHashmap, currentPermissionsHashmap: FiltersHashmap): FolderPermissionLevel | null => {
  const changedKey = Object.keys(updatedPermissionsHashmap).find((key) => {
    return updatedPermissionsHashmap[Number(key)] !== currentPermissionsHashmap[Number(key)];
  });

  if (changedKey === undefined) {
    return null;
  }

  const changedPermission = Number(changedKey);
  return isFolderPermissionLevel(changedPermission) ? changedPermission : null;
};

export const trackFolderPermissionUpdateForInviteLink = (updatedPermissionsHashmap: FiltersHashmap, currentLinkAccessTypesMap: FiltersHashmap): void => {
  const changedPermission = getChangedPermissionLevel(updatedPermissionsHashmap, currentLinkAccessTypesMap);

  if (changedPermission !== null) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_FOLDER_ACCESSLEVEL, {
      [GA4EventParamName.VALUE]: getFolderPermissionLevelValue(changedPermission),
      [GA4EventParamName.TYPE]: 'invitelink',
    });
  }
};

export const trackFolderPermissionUpdateForInviteByEmail = (updatedPermissionsHashmap: FiltersHashmap, currentEmailAccessTypesMap: FiltersHashmap): void => {
  const changedPermission = getChangedPermissionLevel(updatedPermissionsHashmap, currentEmailAccessTypesMap);

  if (changedPermission !== null) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.COLLABORATION_FOLDER_ACCESSLEVEL, {
      [GA4EventParamName.VALUE]: getFolderPermissionLevelValue(changedPermission),
      [GA4EventParamName.TYPE]: 'invitebyemail',
    });
  }
};

export const getFolderPermissionLevelValue = (permissionLevel: FolderPermissionLevel): string => {
  const key = FolderPermissionLevel[permissionLevel] as keyof typeof FolderPermissionLevelValue;
  return FolderPermissionLevelValue[key];
};
